import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { makeAsk, ERRORS, Card, Button, Gate, relative, Avatar, asList } from './dashboardShared';

// #mod: the Mod Dashboard, for the bot owner and the mod role. Opened from the username menu (under TOGΞ Dashboard).
// The bot checks the mod role for every request, whatever the page thinks. Kept simple for now: tools get added here.
// Its member view (the counts and Recently verified) is also the TOGΞ Dashboard's Members tab: MembersPanel.

// The bot's answer to mod.overview, with every list filled in (Firebase drops empty ones)
const tidy = (data) => ({ ...data, you: data?.you || { level: 'Moderator' }, verified: asList(data?.verified) });

const percent = (part, whole) => {
  if (!whole) return '';
  const p = (part / whole) * 100;
  return `${p > 0 && p < 1 ? '<1' : Math.round(p)}% of members`;
};

// The counts, and who's verified most recently
function MembersView({ data }) {
  const g = data?.guild;
  const stats = [
    ['Members', g?.humans?.toLocaleString(), g ? `${g.members.toLocaleString()} with bots` : ''],
    ['Verified', g?.verified != null ? g.verified.toLocaleString() : '—', g?.verified != null ? percent(g.verified, g.humans) : ''],
    ['Online', g?.online?.toLocaleString(), g?.boosts != null ? `${g.boosts} boosts · level ${g.tier}` : ''],
  ];
  return (
    <>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {stats.map(([label, value, sub]) => (
          <Card key={label} className="!p-2.5 sm:!p-4">
            <p className="text-zinc-500 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider">{label}</p>
            <p className="text-white text-base sm:text-xl font-bold truncate">{value ?? '—'}</p>
            <p className="text-zinc-500 text-[10px] sm:text-[11px] truncate">{sub}</p>
          </Card>
        ))}
      </div>

      <Card title="Recently verified">
        {!data ? <p className="text-zinc-500 text-xs">Loading…</p> : data.verified.length === 0 ? <p className="text-zinc-500 text-xs">Nobody's verified yet.</p> : (
          <ul className="divide-y divide-zinc-800">
            {data.verified.map((m) => (
              <li key={m.id} className="flex items-center gap-2 py-2">
                <Avatar src={m.avatar} name={m.name} size={28} />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{m.name}</p>
                  <p className="text-zinc-500 text-[11px] truncate">
                    @{m.username} · {m.verifiedAt ? `verified ${relative(m.verifiedAt)}` : m.joinedAt ? `joined ${relative(m.joinedAt)}` : ''}
                  </p>
                </div>
                {m.onWhatnot && m.whatnot && (
                  <a href={`https://www.whatnot.com/user/${m.whatnot}`} target="_blank" rel="noreferrer"
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full hover:brightness-110" style={{ background: '#fbe510', color: '#000' }}>
                    @{m.whatnot} ↗
                  </a>
                )}
                {m.onWhatnot && !m.whatnot && (
                  // Has the Whatnot role (given by hand, say) but no server name to link to
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#fbe510', color: '#000' }}
                    title="Has the Whatnot role, but their server name isn't set to their Whatnot username">
                    Whatnot · no username
                  </span>
                )}
                {!m.onWhatnot && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-400">No Whatnot</span>}
              </li>
            ))}
          </ul>
        )}
        <p className="text-zinc-600 text-[10px] mt-2">Newest first. Members verified before this list was added show when they joined instead.</p>
      </Card>
    </>
  );
}

// The TOGΞ Dashboard's Members tab: the same view as the Mod Dashboard, with its own refresh
export function MembersPanel({ ask, failed }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const answer = await ask('mod.overview');
    if (!failed(answer)) setData(tidy(answer.data));
  }, [ask, failed]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-zinc-400 text-xs">The same member view your mods see on the Mod Dashboard.</p>
        <Button tone="ghost" small onClick={load}>↻ Refresh</Button>
      </div>
      <MembersView data={data} />
    </div>
  );
}

export default function ModDashboard({ database, discordUser, isMod, getToken, onLogin }) {
  const tokenRef = useRef(getToken); // the same `ask` for the life of the page, as in Dashboard.jsx
  tokenRef.current = getToken;
  const ask = useMemo(() => makeAsk(database, () => tokenRef.current()), [database]);
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState(null);

  const load = async () => {
    const answer = await ask('mod.overview');
    if (answer.ok) { setData(tidy(answer.data)); setProblem(null); }
    else setProblem(answer.error);
  };
  useEffect(() => { if (isMod && getToken()) load(); }, [isMod]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!discordUser) return <Gate title="Mod Dashboard" text="Log in with Discord to use the Mod Dashboard." onLogin={onLogin} />;
  if (!isMod || problem === 'forbidden') return <Gate title="Mod Dashboard" text="The Mod Dashboard is for CHΞΞKSCORD's moderators." />;
  if (problem === 'login' || !getToken()) return <Gate title="Mod Dashboard" text={ERRORS.login} onLogin={onLogin} />;

  const g = data?.guild;
  return (
    <main className="flex-1">
      <div className="max-w-3xl mx-auto px-4 pt-2 pb-10 space-y-4">
        <div className="flex items-center gap-3">
          {g?.icon ? <img src={g.icon} alt="" className="h-10 w-10 rounded-full" /> : <div className="h-10 w-10 rounded-full bg-zinc-800 flex items-center justify-center">🛡️</div>}
          <div className="min-w-0 flex-1">
            <h1 className="text-white text-lg font-bold leading-tight">Mod Dashboard</h1>
            <p className="text-zinc-400 text-[11px]">
              {data ? `${g?.name || 'CHΞΞKSCORD'} · signed in as ${data.you.level.toLowerCase()}` : problem ? ERRORS[problem] || problem : 'Connecting to TOGΞ…'}
            </p>
          </div>
          <Button tone="ghost" small onClick={load}>↻ Refresh</Button>
        </div>

        <MembersView data={data} />

        <Card title="More tools">
          <p className="text-zinc-400 text-xs">Moderation tools will appear here as they're added.</p>
        </Card>
      </div>
    </main>
  );
}
